import { MultiKeyValueComponent, NoValueDash } from '../common';
import * as React from 'react';
import { toHex } from 'ui/common/hex-converter';
import { IconDisplay } from 'ui/common/icon';
import { Label } from 'ui/common/label/label';
import { ValueDisplay } from 'ui';
import { compose } from 'redux';
import get from 'lodash/get';
import styles from './display-codes.scss';

export interface DisplayCodesProps extends MultiKeyValueComponent<number> {
  /**
   * Display modes defined by design handbook
   */
  style?: 'primary' | 'secondary';
  /**
   * When true, label font is normal and the value font is bold and uppercase
   */
  invertStrong?: boolean;
  /**
   * When true, the label is displayed normal and the value font is bold
   */
  strong?: boolean;
  /**
   * When true, the background color defined for the current value is used as background color
   */
  displayBackgroundColor?: boolean;
  /**
   * Currently only 'left' is supported
   */
  displayIcon?: 'left' | 'right';
  /**
   * When true, the label is positioned above the value, as opposed to default, where label is to the left of the value
   */
  showStacked?: boolean;
  /**
   * When true, the component gets hidden when value is empty
   */
  hideIfNoValue?: boolean;
}

const DisplayCodesComponent = (props: DisplayCodesProps) => {
  const {
    className,
    componentId,
    displayBackgroundColor = true,
    displayIcon,
    hideIfNoValue = false,
    label,
    valueItems } = props;

  const backgroundColorOfValue = (valueItem) => (displayBackgroundColor === true && valueItem) && toHex(valueItem.backgroundColor);
  const foregroundColorOfValue = (valueItem) =>  valueItem && toHex(valueItem.foregroundColor);
  const iconUrlOfValue = (valueItem) => displayIcon && get(valueItem, 'icon');

  const iconStyle = displayIcon === 'left' ? styles.displayIconLeft : styles.displayIconRight;

  const items = (valueItems || []).filter(n => n && n.value > 0).map( (valueItem, i) => {
    const separatorString = (valueItems.length > 1 && i < valueItems.length-1 ) ? ', ' : '';
    const colorStyles = {backgroundColor: `${backgroundColorOfValue(valueItem)}`,
                         borderColor:  `${backgroundColorOfValue(valueItem)}`,
                         color: `${backgroundColorOfValue(valueItem) && foregroundColorOfValue(valueItem)}`};
    return (
      !!valueItem.description &&
        <span key={i} className={backgroundColorOfValue(valueItem) ? 'has-bg-color' : ''} style={colorStyles}>
          <IconDisplay classNames={iconStyle} visible={displayIcon} url={iconUrlOfValue(valueItem)} />
          <ValueDisplay valueItem={valueItem} suffix={separatorString}/>
        </span>);
  });

  return (
    !(hideIfNoValue && !items.length) &&
      <div className={className} id={componentId}>
        <Label label={label} />
        {items.length ? <div>{items}</div> : <NoValueDash/>}
      </div>
    );
};

/**
 * A utility that maps known props to a known style and appends them to a list.
 *
 * The list of final style classes are concatenated into a valid JSX className string attribute
 *
 * @param props
 */
const styleFromProps = (props: DisplayCodesProps) =>
  [
    styles.code,
    props.className,
    props.showStacked && styles.stackedColumn,
    props.invertStrong && styles.invertStrong,
    props.strong && !props.invertStrong && styles.strong,
    !!styles[props.style] && `${styles[props.style]} display-codes-styled` ,
    !!get(props, 'valueItem.icon') && props.displayIcon === 'left' && styles.displayIconLeft
  ].filter(s => s).join(' ');

/**
 * Higher order component to override the final className
 *
 * @param Component the component to decorate
 */
const styleVariations = (Component: any) => (props: DisplayCodesProps) => (
  <Component {...props} className={styleFromProps(props)} />
);

export const DisplayCodes = compose(styleVariations)(DisplayCodesComponent);
